import { ApiClientError, api, unwrap } from "@lumberset/api-client";
import { dateTime, money, quantity, unitLabel } from "@lumberset/takeoff-ui";
import {
  Button,
  EmptyState,
  Field,
  Input,
  ProgressBar,
  Prompt,
  StatusBadge,
  Textarea,
  toast,
} from "@lumberset/ui";
import { DocumentText, XMark } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, Fragment, useMemo, useRef, useState } from "react";
import { supplierRfqQuery } from "../lib/queries";
import { putFile } from "../lib/upload";

/** The RFQ as the Supplier sees it. No sign-in: knowing the link is the permission. */
export const Route = createFileRoute("/r/$token")({ component: SupplierRfq });

const MAX_QUOTE_FILE_BYTES = 25 * 1024 * 1024;

/** "Floor Plan, Studs". A group read as a sentence keeps its full stop out of the middle. */
const where = (line: { group: string; use: string }) =>
  `${line.group.replace(/(?<!\.)\.$/, "")}, ${line.use}`;

const sectionTitles: Record<string, string> = {
  walls: "Wall framing",
  floors: "Floor and ceiling framing",
  roof: "Roof framing",
  beams_posts: "Beams and posts",
  sheathing: "Sheathing and subfloor",
  hardware: "Hardware",
  other: "Other",
};

/** "1,250.00" or "$1250" becomes 125000. Blank or unreadable text becomes null. */
function dollarsToCents(text: string): number | null {
  const cleaned = text.trim().replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : null;
}

function SupplierRfq() {
  const { token } = Route.useParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);

  const { data, error, isPending } = useQuery(supplierRfqQuery(token));

  const sections = useMemo(() => {
    const grouped = new Map<string, NonNullable<NonNullable<typeof data>["takeoff"]>["lines"]>();
    for (const line of data?.takeoff?.lines ?? [])
      grouped.set(line.section, [...(grouped.get(line.section) ?? []), line]);
    return [...grouped];
  }, [data]);

  const decline = useMutation({
    mutationFn: () =>
      unwrap(
        api.public.rfq[":token"].respond.$post({ param: { token }, json: { declined: true } }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["public-rfq", token] });
      setEditing(false);
      toast.success("Declined. The builder has been told.");
    },
    onError: (failure) => toast.error(failure.message),
  });

  if (isPending) return <div className="min-h-dvh bg-ui-bg-subtle" />;
  if (error || !data) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ui-bg-subtle">
        <EmptyState
          title="This request is not available"
          description={
            error instanceof ApiClientError && error.status !== 404
              ? error.message
              : "The link may be mistyped, or the builder has withdrawn the request. Ask them for a new link."
          }
        />
      </div>
    );
  }

  const { builder, project, rfq, takeoff, recipient } = data;
  const closed = rfq.status === "closed";
  const answered = recipient.status === "responded" || recipient.status === "declined";
  const showForm = !closed && (!answered || editing);
  const siteAddress = [
    project.siteAddress?.line1,
    project.siteAddress?.city,
    project.siteAddress?.region,
    project.siteAddress?.postalCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="pt-safe pb-safe min-h-dvh bg-ui-bg-subtle px-3 py-6 sm:py-12">
      <article className="mx-auto w-full max-w-[860px] overflow-hidden rounded-xl bg-ui-bg-base shadow-elevation-card-rest">
        <header className="flex flex-col gap-6 border-b border-ui-border-base p-6 sm:flex-row sm:items-start sm:justify-between sm:p-10">
          <div>
            <p className="txt-compact-small text-ui-fg-muted">Request for quote from</p>
            <p className="text-[1.25rem] font-semibold leading-7 tracking-[-0.02em] text-ui-fg-base">
              {builder.name}
            </p>
            <p className="txt-small text-ui-fg-subtle">
              Sent {dateTime(rfq.sentAt)} to{" "}
              {recipient.company || recipient.name || recipient.email}
            </p>
          </div>
          <div className="sm:text-right">
            <p className="txt-compact-small text-ui-fg-muted">Project</p>
            <p className="text-[1.25rem] font-semibold leading-7 tracking-[-0.02em] text-ui-fg-base">
              {project.name}
            </p>
            {/* The date sits beside the badge so a long timestamp never wraps inside it on a phone. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 sm:justify-end">
              {closed ? (
                <>
                  <StatusBadge color="grey">Closed</StatusBadge>
                  <span className="txt-small text-ui-fg-subtle">{dateTime(rfq.closedAt)}</span>
                </>
              ) : recipient.status === "responded" ? (
                <>
                  <StatusBadge color="green">Quoted</StatusBadge>
                  <span className="txt-small text-ui-fg-subtle">
                    {dateTime(recipient.respondedAt)}
                  </span>
                </>
              ) : recipient.status === "declined" ? (
                <StatusBadge color="red">Declined</StatusBadge>
              ) : (
                <StatusBadge color="blue">Open for quotes</StatusBadge>
              )}
            </div>
          </div>
        </header>

        <section className="grid gap-6 border-b border-ui-border-base p-6 sm:grid-cols-2 sm:p-10">
          <div>
            <p className="txt-compact-xsmall-plus mb-1 text-ui-fg-muted">Site</p>
            <p className="txt-compact-medium-plus text-ui-fg-base">{siteAddress || "Not given"}</p>
          </div>
          <div>
            <p className="txt-compact-xsmall-plus mb-1 text-ui-fg-muted">Material list</p>
            <p className="txt-compact-medium-plus text-ui-fg-base">
              {takeoff
                ? `${takeoff.lineCount} lines from ${takeoff.sheetCount} ${
                    takeoff.sheetCount === 1 ? "sheet" : "sheets"
                  } of drawings`
                : "No longer available"}
            </p>
          </div>
          {rfq.message ? (
            <div className="sm:col-span-2">
              <p className="txt-compact-xsmall-plus mb-1 text-ui-fg-muted">From the builder</p>
              <p className="txt-medium whitespace-pre-line text-ui-fg-subtle">{rfq.message}</p>
            </div>
          ) : null}
        </section>

        {takeoff ? (
          <section className="overflow-x-auto">
            <table className="txt-compact-small w-full text-ui-fg-subtle">
              <thead className="txt-compact-small-plus border-b border-ui-border-base bg-ui-bg-subtle text-left">
                <tr>
                  <th className="py-2.5 pl-6 pr-3 font-medium sm:pl-10">Item</th>
                  <th className="px-3 py-2.5 font-medium max-sm:hidden">Where</th>
                  <th className="py-2.5 pl-3 pr-6 text-right font-medium sm:pr-10">Qty</th>
                </tr>
              </thead>
              <tbody>
                {sections.map(([section, lines]) => (
                  <Fragment key={section}>
                    <tr className="border-b border-ui-border-base">
                      <td
                        colSpan={3}
                        className="txt-compact-xsmall-plus px-6 pb-1.5 pt-4 text-ui-fg-muted sm:px-10"
                      >
                        {sectionTitles[section] ?? section}
                      </td>
                    </tr>
                    {lines.map((line) => (
                      <tr key={line.id} className="border-b border-ui-border-base">
                        <td className="py-2.5 pl-6 pr-3 text-ui-fg-base sm:pl-10">
                          {line.description}
                          <span className="txt-compact-xsmall block text-ui-fg-muted sm:hidden">
                            {where(line)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 max-sm:hidden">{where(line)}</td>
                        <td className="tabular whitespace-nowrap py-2.5 pl-3 pr-6 text-right text-ui-fg-base sm:pr-10">
                          {quantity(line.orderQuantity)} {unitLabel(line.unit, line.orderQuantity)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <section className="border-t border-ui-border-base p-6 sm:p-10">
          {answered && !editing ? (
            <Answer
              recipient={recipient}
              closed={closed}
              onChange={() => setEditing(true)}
              onDecline={() => setConfirmDecline(true)}
            />
          ) : closed ? (
            <p className="txt-medium text-ui-fg-subtle">
              This request is closed. {builder.name} is no longer taking quotes for it.
            </p>
          ) : null}

          {showForm ? (
            <ReplyForm
              token={token}
              recipient={recipient}
              onDone={() => setEditing(false)}
              onCancel={answered ? () => setEditing(false) : undefined}
              onDecline={() => setConfirmDecline(true)}
            />
          ) : null}
        </section>

        {takeoff ? (
          <footer className="flex flex-col-reverse gap-3 border-t border-ui-border-base p-6 sm:flex-row sm:justify-between sm:p-10 print:hidden">
            <p className="txt-compact-xsmall self-center text-ui-fg-muted sm:self-auto">
              Quantities were measured from the builder's drawings by LumberSet and checked by our
              Reviewers. Verify them before ordering.
            </p>
            <Button variant="secondary" asChild className="max-sm:w-full">
              <a href={`/api/v1/public/rfq/${token}/pdf`} download>
                <DocumentText /> Download takeoff PDF
              </a>
            </Button>
          </footer>
        ) : null}
      </article>

      <p className="txt-compact-xsmall mt-6 text-center text-ui-fg-muted print:hidden">
        Questions about this request? Reply to the email it came with. Sent through LumberSet.
      </p>

      <Prompt open={confirmDecline} onOpenChange={setConfirmDecline}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Decline this request?</Prompt.Title>
            <Prompt.Description>
              {builder.name} will be told you are not quoting {project.name}. You can still change
              your mind while the request is open.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Go back</Prompt.Cancel>
            <Prompt.Action onClick={() => decline.mutate()}>Decline</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  );
}

type Recipient = NonNullable<
  Awaited<ReturnType<NonNullable<ReturnType<typeof supplierRfqQuery>["queryFn"]>>>
>["recipient"];

/** What this Supplier has answered so far. */
function Answer({
  recipient,
  closed,
  onChange,
  onDecline,
}: {
  recipient: Recipient;
  closed: boolean;
  onChange: () => void;
  onDecline: () => void;
}) {
  const declined = recipient.status === "declined";
  return (
    <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="txt-compact-xsmall-plus mb-1 text-ui-fg-muted">Your reply</p>
        {declined ? (
          <p className="txt-compact-medium-plus text-ui-fg-base">You declined to quote.</p>
        ) : (
          <p className="tabular text-[1.5rem] font-semibold tracking-[-0.02em] text-ui-fg-base">
            {money(recipient.totalCents ?? 0, recipient.currency)}
          </p>
        )}
        {recipient.notes ? (
          <p className="txt-small mt-1 whitespace-pre-line text-ui-fg-subtle">{recipient.notes}</p>
        ) : null}
        {recipient.quoteFileName ? (
          <p className="txt-compact-small mt-2 inline-flex items-center gap-x-1.5 text-ui-fg-subtle">
            <DocumentText /> {recipient.quoteFileName}
          </p>
        ) : null}
        <p className="txt-compact-xsmall mt-2 text-ui-fg-muted">
          {closed
            ? "This request is closed, so the reply can no longer change."
            : `Recorded ${dateTime(recipient.respondedAt)}. You can change it while the request is open.`}
        </p>
      </div>
      {closed ? null : (
        <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row">
          {declined ? null : (
            <Button variant="secondary" onClick={onDecline} className="max-sm:w-full">
              Decline instead
            </Button>
          )}
          <Button variant="secondary" onClick={onChange} className="max-sm:w-full">
            {declined ? "Quote after all" : "Change reply"}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Total, notes and an optional PDF of the Supplier's own quote. */
function ReplyForm({
  token,
  recipient,
  onDone,
  onCancel,
  onDecline,
}: {
  token: string;
  recipient: Recipient;
  onDone: () => void;
  onCancel?: () => void;
  onDecline: () => void;
}) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const respond = useMutation({
    mutationFn: async (form: FormData) => {
      const totalCents = dollarsToCents(String(form.get("total") ?? ""));
      if (totalCents === null) throw new Error("Give your total in dollars, for example 12500.");
      const notes = String(form.get("notes") ?? "").trim() || null;

      // The file goes straight to storage first; the reply then names it so the API can confirm it arrived.
      let quoteFileName: string | null = recipient.quoteFileName ?? null;
      if (file) {
        const { uploadUrl } = await unwrap(
          api.public.rfq[":token"].upload.$post({
            param: { token },
            json: { fileName: file.name, sizeBytes: file.size, contentType: "application/pdf" },
          }),
        );
        setProgress(0);
        await putFile(uploadUrl, file, (fraction) => setProgress(fraction * 100));
        quoteFileName = file.name;
      }

      return unwrap(
        api.public.rfq[":token"].respond.$post({
          param: { token },
          json: { totalCents, notes, quoteFileName, declined: false },
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["public-rfq", token] });
      setProgress(null);
      setFile(null);
      onDone();
      toast.success("Quote sent. The builder has been told.");
    },
    onError: (failure) => {
      setProgress(null);
      toast.error(failure.message);
    },
  });

  function choose(chosen: File | undefined) {
    if (!chosen) return;
    if (chosen.type !== "application/pdf" && !chosen.name.toLowerCase().endsWith(".pdf")) {
      toast.error(`${chosen.name} is not a PDF. Attach your quote as a PDF.`);
    } else if (chosen.size > MAX_QUOTE_FILE_BYTES) {
      toast.error(`${chosen.name} is over 25 MB.`);
    } else {
      setFile(chosen);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    respond.mutate(new FormData(event.currentTarget));
  }

  const busy = respond.isPending;

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-5">
      <div>
        <p className="txt-compact-medium-plus text-ui-fg-base">Your quote</p>
        <p className="txt-small text-ui-fg-subtle">
          Give the total for the whole list. Say in the notes what is included, and attach your own
          quote if you have one.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Total" hint="Including delivery and tax if you charge them.">
          {(control) => (
            <Input
              {...control}
              name="total"
              inputMode="decimal"
              prefix="$"
              required
              size="large"
              defaultValue={
                recipient.totalCents !== null ? (recipient.totalCents / 100).toFixed(2) : ""
              }
              placeholder="0.00"
            />
          )}
        </Field>
        <Field label="Your quote as a PDF" optional>
          {(control) => (
            <div className="flex flex-col gap-y-2">
              <input
                ref={fileInput}
                id={control.id}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={(event) => {
                  choose(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              {file ? (
                <div className="flex h-10 items-center gap-x-2 rounded-md px-3 shadow-borders-base">
                  <DocumentText className="shrink-0 text-ui-fg-muted" />
                  <span className="txt-compact-small min-w-0 flex-1 truncate text-ui-fg-base">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    disabled={busy}
                    onClick={() => setFile(null)}
                    className="transition-fg shrink-0 cursor-pointer rounded p-1 text-ui-fg-muted outline-none hover:bg-ui-bg-base-hover hover:text-ui-fg-subtle focus-visible:shadow-borders-focus"
                  >
                    <XMark />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="large"
                  disabled={busy}
                  onClick={() => fileInput.current?.click()}
                >
                  {recipient.quoteFileName ? `Replace ${recipient.quoteFileName}` : "Choose PDF"}
                </Button>
              )}
            </div>
          )}
        </Field>
      </div>

      <Field label="Notes" optional hint="Lead time, substitutions, what is and is not included.">
        {(control) => (
          <Textarea
            {...control}
            name="notes"
            rows={4}
            maxLength={5000}
            defaultValue={recipient.notes ?? ""}
          />
        )}
      </Field>

      {progress !== null ? (
        <div className="flex flex-col gap-y-2">
          <ProgressBar value={progress} />
          <span className="txt-compact-xsmall tabular text-ui-fg-muted">
            Uploading your quote, {Math.round(progress)}%.
          </span>
        </div>
      ) : null}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {onCancel ? (
          <Button
            type="button"
            variant="transparent"
            size="large"
            disabled={busy}
            onClick={onCancel}
            className="max-sm:w-full"
          >
            Keep my reply
          </Button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="large"
            disabled={busy}
            onClick={onDecline}
            className="max-sm:w-full"
          >
            Decline
          </Button>
        )}
        <Button type="submit" size="large" isLoading={busy} className="max-sm:w-full">
          {recipient.status === "responded" ? "Update quote" : "Send quote"}
        </Button>
      </div>
    </form>
  );
}
